import { formatExclusionReason } from "@/lib/exclusionReason";
import { AgGridReact } from "ag-grid-react";
import {
  AllCommunityModule,
  ModuleRegistry,
  colorSchemeDark,
  colorSchemeLight,
  themeAlpine,
} from "ag-grid-community";
import { memo, useCallback, useEffect, useMemo, useRef } from "react";
import type { CellFocusedEvent, CellKeyDownEvent, ColDef, GridReadyEvent, IRowNode, RowClassParams, RowClickedEvent, SelectionChangedEvent } from "ag-grid-community";
import type { PaperWithRole } from "@/types/paper";
import { useThemeStore } from "@/stores/theme-store";
import { useAngleFitPreviewStore } from "@/stores/angle-fit-preview-store";
import { Badge } from "@/components/ui/badge";
import { autoReadingNote, readingLabel } from "@/lib/reading-labels";
import { seedParentIds } from "@/lib/provenance";
import { bestAngle } from "@/lib/angles";
import { BestAngleBadge } from "@/components/angles/BestAngleBadge";
import type { FacetSummary } from "@/types/concept";
import { BookOpen, Check, CheckCircle, Clock, FileText, Loader2, Lock, Minus, ThumbsDown, ThumbsUp, Trash2, Unlock, Upload, X, XCircle } from "lucide-react";

ModuleRegistry.registerModules([AllCommunityModule]);

const sharedParams = {
  headerFontSize: 11,
  headerFontWeight: 500,
  fontSize: 13,
  oddRowBackgroundColor: "transparent",
  rowBorder: false,
};
const lightBase = themeAlpine.withPart(colorSchemeLight);
const lightTheme = lightBase.withParams ? lightBase.withParams({
  ...sharedParams,
  headerTextColor: "#9ca3af",       // gray-400 — muted headers in light mode
}) : lightBase;
const darkBase = themeAlpine.withPart(colorSchemeDark);
const darkTheme = darkBase.withParams ? darkBase.withParams({
  ...sharedParams,
  headerTextColor: "#6c7086",       // Catppuccin overlay0 — muted headers
  backgroundColor: "#1e1e2e",       // Catppuccin base
  headerBackgroundColor: "#181825", // Catppuccin mantle
  borderColor: "#31324466",         // Surface0 at 40%
  rowHoverColor: "#31324488",       // Surface0 at ~50%
}) : darkBase;

function truncateAuthors(authors: string[]): string {
  if (!authors?.length) return "";
  const shown = authors.slice(0, 3).join(", ");
  return authors.length > 3 ? `${shown} et al.` : shown;
}

/** Extract last name from "First Last" or "First M. Last" for sorting */
function firstAuthorLastName(authors: string[] | undefined): string {
  if (!authors?.length) return "";
  const parts = authors[0].trim().split(/\s+/);
  return (parts[parts.length - 1] ?? "").toLowerCase();
}

const authorComparator = (_a: unknown, _b: unknown, nodeA: { data?: { authors?: string[] } }, nodeB: { data?: { authors?: string[] } }) =>
  firstAuthorLastName(nodeA.data?.authors).localeCompare(firstAuthorLastName(nodeB.data?.authors));

// --- Cell Renderers ---

/** Board 5 (spec 4, 8.1): marks a row the draft angle's sample graded as fitting.
 *  Subscribes to the store itself so a set/clear repaints just these chips —
 *  AG Grid doesn't re-render cells on external store changes. `paperId` is
 *  the row's Paper id. */
export function FitPreviewChip({ paperId, conceptId }: { paperId: string | undefined; conceptId: string | undefined }) {
  const label = useAngleFitPreviewStore((s) =>
    paperId && conceptId && s.conceptId === conceptId && s.paperIds.has(paperId) ? (s.label ?? "") : null);
  if (label == null) return null;
  return (
    <span
      className="ml-1 shrink-0 rounded border border-primary/50 px-1.5 text-[11px] text-primary"
      title={`Sampled while drafting “${label}”`}
    >
      fits the new angle
    </span>
  );
}

function TitleCellRenderer({ data, context }: { data: PaperWithRole; context?: TableCellContext }) {
  const tags = data?.tags;
  const chip = <FitPreviewChip paperId={data?.id} conceptId={context?.conceptId} />;
  if (!tags || tags.length === 0) {
    return (
      <span className="flex items-center gap-1">
        <span className="truncate">{data?.title}</span>
        {data?.is_bridge && (
          <span className="ml-1 shrink-0 inline-block size-2 rounded-full bg-purple-400" />
        )}
        {chip}
      </span>
    );
  }
  // Overlapping colored dots — each subsequent dot shifts left by 4px
  const dotSize = 12;
  const overlap = 6;
  const totalWidth = dotSize + (tags.length - 1) * overlap;
  return (
    <span className="flex items-center gap-1">
      <span
        className="inline-flex flex-shrink-0 items-center"
        style={{ width: totalWidth, minWidth: totalWidth }}
      >
        {tags.map((tag, i) => (
          <span
            key={tag.id}
            className="inline-block rounded-full border border-background"
            style={{
              width: dotSize,
              height: dotSize,
              backgroundColor: tag.color,
              marginLeft: i === 0 ? 0 : -overlap,
              zIndex: tags.length - i,
              position: "relative",
            }}
            title={tag.name}
          />
        ))}
      </span>
      <span className="truncate">{data.title}</span>
      {data.is_bridge && (
        <span className="ml-1 shrink-0 inline-block size-2 rounded-full bg-purple-400" />
      )}
      {chip}
    </span>
  );
}

function ScoreCellRenderer({ data, value, context }: { data: PaperWithRole; value: number; context?: TableCellContext }) {
  const angles = context?.angles;
  const showBadge = !!data && !!angles && angles.length >= 2 && !!bestAngle(data, angles);
  if (!value && !showBadge) return null;
  const isOverride = data?.user_score_override != null;
  let cls = "bg-muted text-muted-foreground";
  if (isOverride) {
    cls = "bg-blue-100 text-blue-800 ring-1 ring-blue-300 dark:bg-blue-900/30 dark:text-blue-400 dark:ring-blue-700";
  } else if (value >= 80) {
    cls = "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400";
  } else if (value >= 50) {
    cls = "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400";
  }
  return (
    <span className="flex min-w-0 items-center">
      {value ? <span className={`inline-flex shrink-0 items-center rounded px-1.5 py-0.5 text-xs font-medium ${cls}`}>{value}</span> : null}
      {showBadge && <BestAngleBadge paper={data} angles={angles} />}
    </span>
  );
}

function OaIconRenderer({ value }: { value: boolean }) {
  return value ? (
    <Unlock className="size-4 text-green-500" />
  ) : (
    <Lock className="size-4 text-muted-foreground" />
  );
}

function ContributionTypeBadge({ value }: { value: string }) {
  if (!value) return null;
  return (
    <Badge variant="secondary" className="text-xs">
      {value}
    </Badge>
  );
}

const READING_STATUS_CYCLE = ["unread", "skimmed", "read"] as const;

function ReadingStatusBadge({
  data,
  context,
}: {
  data: PaperWithRole;
  context: TableCellContext;
}) {
  if (!context?.onReadingStatus) return null;
  const status = data?.reading_status ?? "unread";
  const label = readingLabel(status);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const idx = READING_STATUS_CYCLE.indexOf(
      status as (typeof READING_STATUS_CYCLE)[number],
    );
    const next = READING_STATUS_CYCLE[(idx + 1) % READING_STATUS_CYCLE.length];
    context.onReadingStatus!(data.id, next);
  };

  if (status === "unread") {
    return (
      <button
        onClick={handleClick}
        className="inline-flex size-full items-center justify-center text-xs"
        title={`${label} — click to change`}
      >
        <span className="inline-block size-2 rounded-full border border-gray-300 dark:border-gray-600" />
      </button>
    );
  }

  const dotColor = status === "read" ? "bg-green-500" : "bg-yellow-500";
  const letter = status === "read" ? "R" : "S";
  const textColor = status === "read" ? "text-green-600 dark:text-green-400" : "text-yellow-600 dark:text-yellow-400";

  return (
    <button
      onClick={handleClick}
      className="inline-flex size-full items-center justify-center gap-1 text-xs"
      title={
        autoReadingNote(status, data.reading_status_source)
          ? `${label} (set automatically) — click to change`
          : `${label} — click to change`
      }
    >
      <span className={`inline-block size-2 rounded-full ${dotColor}`} />
      <span className={`font-medium ${textColor}`}>{letter}</span>
    </button>
  );
}

interface TableCellContext {
  onScoreOverride?: (id: string, score: number) => void;
  onReadingStatus?: (id: string, status: string) => void;
  /** Current seed paper ids — read by the Seeds column. */
  seedIds?: Set<string>;
  /** Facets 1c-2: active angles (2+) for the best-angle badge. */
  angles?: FacetSummary[];
  /** Board 5: the concept this table lists — gates the fit-preview chip. */
  conceptId?: string;
}


function QuickScoreRenderer({
  data,
  context,
}: {
  data: PaperWithRole;
  context: TableCellContext;
}) {
  if (!context?.onScoreOverride) return null;
  const override = data?.user_score_override;
  const isChampion = override != null && override >= 70;
  const isDismissed = override != null && override <= 30;
  return (
    <span className="inline-flex items-center gap-1">
      <button
        onClick={(e) => {
          e.stopPropagation();
          context.onScoreOverride!(data.id, isChampion ? 0 : 85);
        }}
        title={isChampion ? "Clear relevant mark" : "Mark relevant (→)"}
      >
        <ThumbsUp
          className={`size-3.5 ${isChampion ? "fill-green-500 text-green-500" : "text-muted-foreground hover:text-green-400"}`}
        />
      </button>
      <button
        onClick={(e) => {
          e.stopPropagation();
          context.onScoreOverride!(data.id, isDismissed ? 0 : 15);
        }}
        title={isDismissed ? "Clear not-relevant mark" : "Mark not relevant (←)"}
      >
        <ThumbsDown
          className={`size-3.5 ${isDismissed ? "fill-red-500 text-red-500" : "text-muted-foreground hover:text-red-400"}`}
        />
      </button>
    </span>
  );
}

function FullTextBadge({ data }: { data: PaperWithRole }) {
  const status = data?.canonical_pdf_status;
  if (!status || status === "Idle") return null;

  let label = status;
  let variant: "default" | "destructive" | "secondary" = "secondary";
  let pulse = "";

  switch (status) {
    case "Success":
      label = data.canonical_pdf_source === "client_upload" ? "Uploaded" : "PDF";
      variant = "default";
      break;
    case "Full Text":
      label = "Full Text";
      variant = "default";
      break;
    case "Not Available":
      label = "HTML";
      variant = "secondary";
      break;
    case "Paywalled":
      label = "Paywall";
      variant = "destructive";
      break;
    case "Unavailable":
      label = "Not Found";
      variant = "destructive";
      break;
    case "Fetching":
      pulse = " animate-pulse";
      break;
  }

  return (
    <Badge variant={variant} className={`text-xs${pulse}`}>
      {label}
    </Badge>
  );
}

function PdfStatusBadge({ data }: { data: PaperWithRole }) {
  const status = data?.canonical_pdf_status;
  const source = data?.pdf_available_source;
  // Legacy client uploads landed on the canonical row before pdf_available_source
  // existed; treat them the same as a user-private PDF.
  const isUserUpload =
    source === "user" || data?.canonical_pdf_source === "client_upload";
  const isOpenAccess = !isUserUpload && (source === "canonical" || status === "Success");

  if (isUserUpload)
    return (
      <span title="Uploaded — private to you" className="inline-flex items-center gap-1 text-[11px] text-violet-500">
        <Upload className="size-3" />PDF
      </span>
    );
  if (isOpenAccess)
    return (
      <span title="Open access" className="inline-flex items-center gap-1 text-[11px] text-green-500">
        <BookOpen className="size-3" />PDF
      </span>
    );
  if (!status || status === "Idle")
    return <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground"><Minus className="size-3" />—</span>;
  if (status === "Fetching")
    return <span className="inline-flex items-center gap-1 text-[11px] text-yellow-500"><Clock className="size-3 animate-pulse" />Fetching</span>;
  if (status === "Full Text")
    return <span className="inline-flex items-center gap-1 text-[11px] text-green-500"><CheckCircle className="size-3" />Text</span>;
  if (status === "Paywalled")
    return <span className="inline-flex items-center gap-1 text-[11px] text-amber-500"><Lock className="size-3" />Paywall</span>;
  if (status === "Unavailable" || status === "Not Available")
    return <span className="inline-flex items-center gap-1 text-[11px] text-red-500/70"><XCircle className="size-3" />N/A</span>;
  return null;
}

function CitesRenderer({ data }: { data: PaperWithRole }) {
  const count = data?.citations;
  if (count == null) return null;
  return (
    <span className="inline-flex items-center gap-0.5 tabular-nums text-xs text-muted-foreground">
      {data.is_influential && <Check className="size-3 text-green-500" />}
      {count}
    </span>
  );
}

// Retain renderers for potential use in other column sets
void OaIconRenderer, ContributionTypeBadge, FullTextBadge, FileText, Loader2, X;

// --- Column Definitions ---

const foundationalColumns: ColDef<PaperWithRole>[] = [
  { field: "title", headerName: "Title", flex: 3, minWidth: 200 },
  {
    field: "authors",
    headerName: "Authors",
    flex: 2,
    minWidth: 150,
    valueFormatter: (p) => truncateAuthors(p.value),
    comparator: authorComparator,
    cellStyle: { display: "flex", alignItems: "center" },
  },
  { field: "year", headerName: "Year", width: 80, cellStyle: { display: "flex", alignItems: "center" } },
  { field: "journal", headerName: "Journal", flex: 1, minWidth: 120 },
  { field: "citations", headerName: "Citations", width: 100, sort: "desc" },
  {
    field: "canonical_pdf_status",
    headerName: "PDF",
    width: 75,
    cellRenderer: PdfStatusBadge,
    headerClass: "ag-header-cell-center",
    cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
  },
];

function BridgeTitleHeader() {
  return (
    <span className="flex items-center gap-1">
      Title
      <span className="ml-1 text-[10px] text-muted-foreground flex items-center gap-1">
        (<span className="inline-block size-2 rounded-full bg-purple-400" /> = bridge paper spanning at least 2 arguments)
      </span>
    </span>
  );
}

function getExpandColumns(hasBridge = false, showAngles = false): ColDef<PaperWithRole>[] {
  return [
    {
      colId: "title",
      headerName: "Title",
      ...(hasBridge ? { headerComponent: BridgeTitleHeader } : {}),
      flex: 3,
      minWidth: 150,
      tooltipValueGetter: (p) => p.data?.title ?? "",
      comparator: (_a, _b, nodeA, nodeB) => {
        const strip = (s: string) => s.replace(/^[\p{P}\p{S}]+/u, "");
        return strip(nodeA.data?.title ?? "").localeCompare(strip(nodeB.data?.title ?? ""));
      },
      valueGetter: (p) => {
        const tags = p.data?.tags;
        const tagKey = tags?.length ? tags.map((t) => `${t.id}:${t.color}`).join(",") : "";
        return `${tagKey}|${p.data?.title ?? ""}`;
      },
      cellRenderer: TitleCellRenderer,
    },
    {
      field: "authors",
      headerName: "Authors",
      flex: 1,
      minWidth: 80,
      valueFormatter: (p) => truncateAuthors(p.value),
      comparator: authorComparator,
      cellClass: "text-muted-foreground !text-xs",
      cellStyle: { display: "flex", alignItems: "center" },
    },
    { field: "year", headerName: "Year", width: 70, cellClass: "text-muted-foreground !text-xs", cellStyle: { display: "flex", alignItems: "center" } },
    {
      colId: "score",
      headerName: "Score",
      width: showAngles ? 150 : 75,
      valueGetter: (p) => p.data?.user_score_override ?? p.data?.normalized_score ?? 0,
      cellRenderer: ScoreCellRenderer,
      sortingOrder: ["desc", "asc", null],
    },
    {
      colId: "judgement",
      headerName: "",
      headerTooltip: "Mark relevant / not relevant",
      width: 56,
      sortable: false,
      resizable: false,
      cellRenderer: QuickScoreRenderer,
      headerClass: "ag-header-cell-center",
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
    },
    {
      colId: "seeds",
      headerName: "Seeds",
      headerTooltip: "How many of your current seeds led to this paper",
      width: 70,
      valueGetter: (p) =>
        seedParentIds(p.data?.discovered_via, (p.context as TableCellContext)?.seedIds ?? new Set()).length,
      valueFormatter: (p) => {
        const n = (p.context as TableCellContext)?.seedIds?.size ?? 0;
        return p.data?.discovered_via ? `${p.value}/${n}` : "—";
      },
      cellClass: "text-muted-foreground !text-xs",
      headerClass: "ag-header-cell-center",
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
      sortingOrder: ["desc", "asc", null],
    },
    {
      field: "reading_status",
      headerName: "",
      width: 45,
      cellRenderer: ReadingStatusBadge,
      headerClass: "ag-header-cell-center",
      sortable: false,
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
    },
    {
      field: "citations",
      headerName: "Cites",
      width: 60,
      cellRenderer: CitesRenderer,
      headerClass: "ag-header-cell-center",
      cellStyle: { display: "flex", justifyContent: "center" },
    },
    {
      field: "canonical_pdf_status",
      headerName: "PDF",
      width: 75,
      cellRenderer: PdfStatusBadge,
      headerClass: "ag-header-cell-center",
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
      sortingOrder: ["desc", "asc", null],
      comparator: (_a, _b, nodeA, nodeB) => {
        const rank = (d: PaperWithRole | undefined): number => {
          if (!d) return -1;
          if (d.pdf_available_source === "user" || d.canonical_pdf_source === "client_upload") return 5;
          if (d.pdf_available_source === "canonical") return 4;
          const status = d.canonical_pdf_status ?? "Idle";
          const byStatus: Record<string, number> = {
            Success: 4, "Full Text": 3, Fetching: 2, Paywalled: 1,
            Unavailable: 0, "Not Available": 0, Idle: -1,
          };
          return byStatus[status] ?? -1;
        };
        return rank(nodeA.data) - rank(nodeB.data);
      },
    },
  ];
}

function getDismissedColumns(): ColDef<PaperWithRole>[] {
  return [
    {
      field: "title",
      headerName: "Title",
      flex: 2,
      minWidth: 200,
      cellRenderer: (params: { value: string }) => (
        <span className="truncate">{params.value}</span>
      ),
    },
    {
      field: "authors",
      headerName: "Authors",
      flex: 1,
      minWidth: 120,
      valueFormatter: (params: { value: string[] }) =>
        params.value?.join(", ") ?? "",
      comparator: (_va: unknown, _vb: unknown, a: { data?: PaperWithRole }, b: { data?: PaperWithRole }) => {
        const aLast = (a.data?.authors?.[0] ?? "").split(" ").pop() ?? "";
        const bLast = (b.data?.authors?.[0] ?? "").split(" ").pop() ?? "";
        return aLast.localeCompare(bLast);
      },
    },
    { field: "year", headerName: "Year", width: 70 },
    {
      colId: "score",
      headerName: "Score",
      width: 75,
      valueGetter: (p) => p.data?.user_score_override ?? p.data?.normalized_score ?? 0,
      cellRenderer: ScoreCellRenderer,
      sort: "desc",
      sortingOrder: ["desc", "asc", null],
    },
    {
      field: "exclusion_reason",
      headerName: "Reason",
      flex: 1,
      minWidth: 150,
      cellRenderer: (params: { value: string | null }) => (
        <span className="text-muted-foreground text-xs">{formatExclusionReason(params.value)}</span>
      ),
    },
  ];
}

// --- Helpers ---

/** Clear all ag-row-active classes, then apply to the target row.
 *  Clearing globally avoids stale highlights from AG Grid DOM recycling. */
function setRowActive(rowId: string | undefined, active: boolean) {
  if (active) {
    // Clear all existing active highlights first
    document.querySelectorAll(".ag-row-active").forEach((el) => {
      el.classList.remove("ag-row-active");
    });
    if (!rowId) return;
    // Apply to all elements for this row (pinned + center containers)
    document.querySelectorAll(`[row-id="${rowId}"]`).forEach((el) => {
      el.classList.add("ag-row-active");
    });
  } else {
    if (!rowId) return;
    document.querySelectorAll(`[row-id="${rowId}"]`).forEach((el) => {
      el.classList.remove("ag-row-active");
    });
  }
}

// --- Props ---

interface Props {
  papers: PaperWithRole[];
  loading?: boolean;
  emptyMessage?: string;
  mode?: "foundational" | "expand";
  onRowClick?: (paper: PaperWithRole) => void;
  onScoreOverride?: (paperId: string, score: number) => void;
  onReadingStatus?: (paperId: string, status: string) => void;
  selectedIds?: Set<string>;
  onSelectionChange?: (ids: string[]) => void;
  activePaperId?: string;
  height?: number | string;
  bottomOffset?: number;
  onRemovePaper?: (paperId: string) => void;
  dismissedView?: boolean;
  onRestore?: (paperId: string) => void;
  flaggedView?: boolean;
  onUnflag?: (paperId: string) => void;
  /** Rows to mark with the recently-added highlight (green tint). */
  recentPaperIds?: Set<string>;
  /** Bump to scroll the table to the top row — "surface the newest papers"
   * actions must be observable even when the sort is already added_at desc
   * (their sort request is then a no-op). */
  scrollTopTick?: number;
  /** Current seed paper ids — drives the expand-mode Seeds column. */
  seedIds?: Set<string>;
  /** Facets 1c-2: active angles (pass only at 2+). */
  angles?: FacetSummary[];
  /** Board 5: the concept these rows belong to (fit-preview chips). */
  conceptId?: string;
  /** Change to clear any AG Grid header sort (URL sort is the source of truth; the angle tab ranks by angle). */
  sortResetKey?: string;
}

export const PaperTable = memo(function PaperTable({
  papers,
  loading,
  emptyMessage,
  mode = "foundational",
  onRowClick,
  onScoreOverride,
  onReadingStatus,
  selectedIds,
  onSelectionChange,
  activePaperId,
  height,
  bottomOffset,
  onRemovePaper,
  dismissedView,
  onRestore,
  flaggedView,
  onUnflag,
  recentPaperIds,
  scrollTopTick,
  seedIds,
  angles,
  sortResetKey,
  conceptId,
}: Props) {
  const resolved = useThemeStore((s) => s.resolved);
  const gridRef = useRef<AgGridReact>(null);
  const isSettingSelection = useRef(false);
  const isMounted = useRef(true);

  // Track the currently highlighted row ID (may differ from activePaperId during rapid nav)
  const highlightedId = useRef(activePaperId);
  // Debounce timer for PaperDetail updates during keyboard navigation
  const navDebounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  // Track whether last focus change was from keyboard (vs click)

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      clearTimeout(navDebounceRef.current);
    };
  }, []);

  const hasBridge = papers.some((p) => p.is_bridge);
  const showAngles = (angles?.length ?? 0) >= 2;

  const columnDefs = useMemo<ColDef<PaperWithRole>[]>(() => {
    // Dismissed view — simplified columns with restore action
    if (dismissedView) {
      const cols = getDismissedColumns();
      if (onRestore) {
        cols.push({
          headerName: "",
          width: 80,
          sortable: false,
          resizable: false,
          cellRenderer: (params: { data?: PaperWithRole }) => params.data ? (
            <button
              onClick={(e) => { e.stopPropagation(); onRestore(params.data!.id); }}
              className="rounded px-2 py-0.5 text-xs text-blue-400 hover:bg-blue-500/10 transition-colors"
            >
              Restore
            </button>
          ) : null,
        } as ColDef<PaperWithRole>);
      }
      return cols;
    }
    // Flagged view — simplified columns with unflag action
    if (flaggedView) {
      const cols: ColDef<PaperWithRole>[] = [
        { field: "title", headerName: "Title", flex: 2, minWidth: 200,
          cellRenderer: (params: { value: string }) => <span className="truncate">{params.value}</span> },
        { field: "authors", headerName: "Authors", flex: 1, minWidth: 120,
          valueFormatter: (params: { value: string[] }) => params.value?.join(", ") ?? "",
          comparator: (_va: unknown, _vb: unknown, a: { data?: PaperWithRole }, b: { data?: PaperWithRole }) => {
            const aLast = (a.data?.authors?.[0] ?? "").split(" ").pop() ?? "";
            const bLast = (b.data?.authors?.[0] ?? "").split(" ").pop() ?? "";
            return aLast.localeCompare(bLast);
          } },
        { field: "year", headerName: "Year", width: 70 },
      ];
      if (onUnflag) {
        cols.push({
          headerName: "",
          width: 80,
          sortable: false,
          resizable: false,
          cellRenderer: (params: { data?: PaperWithRole }) => params.data ? (
            <button
              onClick={(e) => { e.stopPropagation(); onUnflag(params.data!.id); }}
              className="rounded px-2 py-0.5 text-xs text-amber-400 hover:bg-amber-500/10 transition-colors"
            >
              Unflag
            </button>
          ) : null,
        } as ColDef<PaperWithRole>);
      }
      return cols;
    }
    if (mode === "expand") {
      const cols = getExpandColumns(hasBridge, showAngles);
      if (onSelectionChange) {
        return [
          {
            headerCheckboxSelection: true,
            checkboxSelection: true,
            width: 40,
            pinned: "left",
            suppressSizeToFit: true,
            sortable: false,
            resizable: false,
          },
          ...cols,
        ];
      }
      return cols;
    }
    if (onRemovePaper) {
      return [
        ...foundationalColumns,
        {
          headerName: "",
          width: 50,
          sortable: false,
          resizable: false,
          cellRenderer: ({ data }: { data: PaperWithRole }) => {
            if (!data) return null;
            return (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onRemovePaper(data.id);
                }}
                className="flex size-full items-center justify-center text-muted-foreground hover:text-destructive transition-colors"
                title="Remove paper"
              >
                <Trash2 className="size-3.5" />
              </button>
            );
          },
        },
      ];
    }
    return foundationalColumns;
  }, [mode, onSelectionChange, onRemovePaper, hasBridge, showAngles, dismissedView, onRestore, flaggedView, onUnflag]);

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      resizable: true,
    }),
    [],
  );

  const getRowId = useCallback(
    (params: { data: PaperWithRole }) => params.data.id,
    [],
  );

  // Sync programmatic selectedIds with AG Grid checkbox state
  const syncSelection = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api || !selectedIds) return;
    isSettingSelection.current = true;
    api.forEachNode((node) => {
      const shouldSelect = selectedIds.has(node.data?.id);
      if (node.isSelected() !== shouldSelect) {
        node.setSelected(shouldSelect);
      }
    });
    isSettingSelection.current = false;
  }, [selectedIds]);

  const handleGridReady = useCallback(
    (_event: GridReadyEvent) => {
      syncSelection();
    },
    [syncSelection],
  );

  useEffect(() => {
    syncSelection();
  }, [syncSelection, papers]);

  // Row click: immediate highlight + immediate PaperDetail update
  const handleRowClicked = useCallback(
    (event: RowClickedEvent<PaperWithRole>) => {
      if (!onRowClick || !event.data) return;
      const target = event.event?.target as HTMLElement | undefined;
      if (target?.closest(".ag-selection-checkbox, .ag-checkbox, button, .reading-status-cell")) return;
      // Cancel any pending keyboard debounce
      clearTimeout(navDebounceRef.current);
      // Update highlight
      setRowActive(highlightedId.current, false);
      highlightedId.current = event.data.id;
      setRowActive(event.data.id, true);
      onRowClick(event.data);
    },
    [onRowClick],
  );

  // Cell focus: fires for BOTH keyboard nav and click-into-cell.
  // We use this to track keyboard navigation — AG Grid handles arrow keys natively.
  const handleCellFocused = useCallback(
    (event: CellFocusedEvent) => {
      if (!onRowClick || event.rowIndex == null) return;
      const api = gridRef.current?.api;
      if (!api) return;
      const node = api.getDisplayedRowAtIndex(event.rowIndex);
      if (!node?.data) return;
      const newId = (node.data as PaperWithRole).id;

      // Skip if same row (e.g. clicking different cells in same row)
      if (newId === highlightedId.current) return;

      // Update highlight immediately via DOM
      setRowActive(highlightedId.current, false);
      highlightedId.current = newId;
      setRowActive(newId, true);

      // Debounce the PaperDetail update for keyboard nav.
      // For clicks, handleRowClicked already set highlightedId so we never reach here (same-row check above).
      clearTimeout(navDebounceRef.current);
      const data = node.data as PaperWithRole;
      navDebounceRef.current = setTimeout(() => {
        onRowClick(data);
      }, 150);
    },
    [onRowClick],
  );

  // Track last score override for single-step undo via 'u' key
  const lastOverrideAction = useRef<{ paperId: string; previousScore: number } | null>(null);

  const handleCellKeyDown = useCallback(
    (event: CellKeyDownEvent<PaperWithRole>) => {
      const kbEvent = event.event as KeyboardEvent | undefined;
      if (!kbEvent) return;

      // Undo last score override with 'u' key
      if (onScoreOverride && kbEvent.key === "u" && lastOverrideAction.current) {
        kbEvent.preventDefault();
        const { paperId, previousScore } = lastOverrideAction.current;
        onScoreOverride(paperId, previousScore);
        lastOverrideAction.current = null;
        return;
      }

      // Quick score shortcuts: ← = dismiss (15), → = champion (85)
      if (onScoreOverride && event.data && (kbEvent.key === "ArrowLeft" || kbEvent.key === "ArrowRight")) {
        kbEvent.preventDefault();
        const override = event.data.user_score_override;
        lastOverrideAction.current = {
          paperId: event.data.id,
          previousScore: override ?? 0,
        };
        if (kbEvent.key === "ArrowRight") {
          onScoreOverride(event.data.id, override != null && override >= 70 ? 0 : 85);
        } else {
          onScoreOverride(event.data.id, override != null && override <= 30 ? 0 : 15);
        }
        return;
      }

    },
    [onScoreOverride],
  );

  const handleSelectionChanged = useCallback(
    (event: SelectionChangedEvent<PaperWithRole>) => {
      if (onSelectionChange && !isSettingSelection.current && isMounted.current) {
        const selected = event.api.getSelectedRows();
        onSelectionChange(selected.map((r) => r.id));
      }
      // Re-apply active class — selection changes may recreate row DOM nodes
      if (highlightedId.current) {
        requestAnimationFrame(() => setRowActive(highlightedId.current, true));
      }
    },
    [onSelectionChange],
  );

  // Wrap onScoreOverride to record undo state for clicks too
  const wrappedScoreOverride = useCallback(
    (id: string, score: number) => {
      if (!onScoreOverride) return;
      const api = gridRef.current?.api;
      const node = api?.getRowNode(id);
      const currentOverride = node?.data?.user_score_override ?? 0;
      lastOverrideAction.current = { paperId: id, previousScore: currentOverride };
      onScoreOverride(id, score);
    },
    [onScoreOverride],
  );

  const gridContext = useMemo<TableCellContext>(
    () => ({ onScoreOverride: onScoreOverride ? wrappedScoreOverride : undefined, onReadingStatus, seedIds, angles, conceptId }),
    [onScoreOverride, wrappedScoreOverride, onReadingStatus, seedIds, angles, conceptId],
  );

  // AG Grid doesn't re-run valueGetters when context changes — repaint the
  // Seeds column when the seed set does (promote/demote changes the n in k/n).
  useEffect(() => {
    const api = gridRef.current?.api;
    if (!api || api.isDestroyed()) return;
    api.refreshCells({ columns: ["seeds"], force: true });
  }, [seedIds]);

  // AG Grid doesn't re-run renderers when context changes.
  useEffect(() => {
    const api = gridRef.current?.api;
    if (!api || api.isDestroyed()) return;
    api.refreshCells({ columns: ["score"], force: true });
  }, [angles]);

  // A header-click sort would override the angle tab's ranking (the Score
  // column sorts by the overall); clear it when the tab changes.
  const lastSortReset = useRef(sortResetKey);
  useEffect(() => {
    if (lastSortReset.current === sortResetKey) return;
    lastSortReset.current = sortResetKey;
    const api = gridRef.current?.api;
    if (!api || api.isDestroyed()) return;
    api.applyColumnState({ defaultState: { sort: null } });
  }, [sortResetKey]);

  // Sync highlight when activePaperId prop changes (e.g. from external state reset)
  useEffect(() => {
    if (activePaperId !== highlightedId.current) {
      setRowActive(highlightedId.current, false);
      highlightedId.current = activePaperId;
      setRowActive(activePaperId, true);
      // Scroll the row into view — the deep-link consumer in expand.tsx
      // sets activePaperId for a paper that may be far down a virtualized
      // list, and the lavender border alone is invisible if it's offscreen.
      if (activePaperId) {
        const api = gridRef.current?.api;
        const node = api?.getRowNode(activePaperId);
        if (node) api?.ensureNodeVisible(node, "middle");
      }
    }
  }, [activePaperId]);

  // Re-apply highlight after AG Grid re-renders rows (data change, virtual scroll)
  const handleRowDataUpdated = useCallback(() => {
    if (highlightedId.current) {
      setRowActive(highlightedId.current, true);
    }
  }, []);

  // Recently-added highlight. getRowClass reads a render-phase ref mirror so
  // rows created by a rowData update (papers landing mid-import) classify
  // against the CURRENT set; the effect redraws only the rows whose
  // membership changed (chip dismiss, re-arm) — already-rendered rows don't
  // re-evaluate getRowClass on a prop change alone.
  const recentIdsRef = useRef(recentPaperIds);
  recentIdsRef.current = recentPaperIds;
  const prevRecentRef = useRef<Set<string> | undefined>(undefined);
  useEffect(() => {
    const prev = prevRecentRef.current;
    prevRecentRef.current = recentPaperIds;
    const api = gridRef.current?.api;
    if (!api || api.isDestroyed()) return;
    const affected: IRowNode<PaperWithRole>[] = [];
    api.forEachNode((node) => {
      const id = node.data?.id;
      if (!id) return;
      if ((prev?.has(id) ?? false) !== (recentPaperIds?.has(id) ?? false)) {
        affected.push(node);
      }
    });
    if (affected.length) api.redrawRows({ rowNodes: affected });
  }, [recentPaperIds]);

  const getRowClass = useCallback(
    (params: RowClassParams<PaperWithRole>) =>
      params.data && recentIdsRef.current?.has(params.data.id)
        ? "row-recently-added"
        : undefined,
    [],
  );

  // Scroll to the top row when the tick bumps (skip the initial mount value).
  const prevScrollTickRef = useRef(scrollTopTick);
  useEffect(() => {
    if (scrollTopTick === prevScrollTickRef.current) return;
    prevScrollTickRef.current = scrollTopTick;
    const api = gridRef.current?.api;
    if (!api || api.isDestroyed()) return;
    if (api.getDisplayedRowCount() > 0) api.ensureIndexVisible(0, "top");
  }, [scrollTopTick]);

  const resolvedHeight = height ?? (mode === "foundational" ? 400 : `calc(100vh - 280px - ${bottomOffset ?? 0}px)`);

  return (
    <div className="w-full" style={{ height: resolvedHeight }}>
      <AgGridReact<PaperWithRole>
        ref={gridRef}
        theme={resolved === "dark" ? darkTheme : lightTheme}
        rowData={papers}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        context={gridContext}
        loading={loading}
        overlayNoRowsTemplate={emptyMessage ?? "No papers found."}
        getRowId={getRowId}
        getRowClass={getRowClass}
        rowBuffer={20}
        rowSelection={onSelectionChange ? "multiple" : undefined}
        suppressRowClickSelection
        onGridReady={handleGridReady}
        onRowClicked={handleRowClicked}
        onCellFocused={handleCellFocused}
        onCellKeyDown={handleCellKeyDown}
        onSelectionChanged={handleSelectionChanged}
        onRowDataUpdated={handleRowDataUpdated}
        isRowSelectable={() => true}
      />
    </div>
  );
});
